import React, { useCallback, useMemo, useState } from "react";

import {
  HvFormStatus,
  HvInput,
  HvInputProps,
  InputSuggestion,
} from "@hitachivantara/uikit-react-core";

import { UsersDomain, useUsers } from "lib/api";
import { useUniqueId } from "lib/utils/ids";

import useStyles from "./styles";

function* filterTopX<T>(
  array: T[],
  predicate: (value: T, index: number, array: T[]) => unknown,
  limit: number
) {
  const maxSize = !limit || limit > array.length ? array.length : limit;

  let count = 0;
  let i = 0;
  while (count < maxSize && i < array.length) {
    if (predicate(array[i], i, array)) {
      yield array[i];
      // eslint-disable-next-line no-plusplus
      ++count;
    }

    // eslint-disable-next-line no-plusplus
    ++i;
  }
}

export interface UserSearchInputProps extends HvInputProps {
  id?: string;
  noMatchLabel: string;
  filterPredicate: (user: UsersDomain, searchString: string) => boolean;
  formatUser: (user: UsersDomain) => React.ReactNode;
  validateUser?: (user: UsersDomain) => string | null;
  onUserSelected: (user: UsersDomain) => void;
  allowExternal?: boolean;
  validateExternal?: (externalUser: string) => string | null;
  onExternalSelected?: (externalUser: string) => void;
}

const UserSearchInput: React.FC<UserSearchInputProps> = ({
  id,
  noMatchLabel,
  filterPredicate,
  formatUser,
  validateUser,
  onUserSelected,
  allowExternal = false,
  validateExternal,
  onExternalSelected,
  ...others
}) => {
  const classes = useStyles();

  const elementId = useUniqueId(id, "user-search-input");

  const { data: userData } = useUsers(
    {
      shouldRetryOnError: false,
    },
    {
      pageNr: -1,
      pageSize: -1,
      orderBy: encodeURIComponent("name,surname,email"),
      Order: "asc",
      includeGroups: "true",
      isExcludeServiceAccount: "true",
    }
  );

  const userList = useMemo(() => userData?.data ?? [], [userData?.data]);

  const suggestionHandler = useCallback(
    (val: string): InputSuggestion[] => {
      if (typeof val !== "string" || val.trim() === "") return [];

      const filteredTop5 = [
        ...filterTopX(userList, (user) => filterPredicate(user, val), 5),
      ];

      if (filteredTop5.length === 0)
        return [{ id: val, label: noMatchLabel, value: val }];

      return filteredTop5.map((user) => ({
        id: user.id,
        // this is cheating: ui kit ts def is wrong, we know it supports node
        label: formatUser(user) as string,
        value: user.email,
      }));
    },
    [filterPredicate, formatUser, noMatchLabel, userList]
  );

  const [recipientInputValue, setRecipientInputValue] = useState("");
  const [recipientInputStatus, setRecipientInputStatus] =
    useState<HvFormStatus>("valid");
  const [recipientInputStatusMessage, setRecipientInputStatusMessage] =
    useState("");

  const onChange = useCallback(
    (
      event:
        | React.KeyboardEvent<HTMLInputElement>
        | React.MouseEvent<HTMLButtonElement>
        | React.ChangeEvent<HTMLInputElement>,
      inputValue: string
    ) => {
      const value = inputValue.trim();
      if (
        value.trim() !== "" &&
        // click: user selected suggestion
        // keydown: user pressed Enter
        (event.type === "click" || event.type === "keydown")
      ) {
        const user = userList.find((u) => filterPredicate(u, value));
        if (user) {
          const errorMessage = validateUser?.(user);
          if (errorMessage == null) {
            setRecipientInputStatus("valid");
            setRecipientInputValue("");

            onUserSelected(user);
          } else {
            setRecipientInputStatus("invalid");
            setRecipientInputStatusMessage(errorMessage);
            setRecipientInputValue(value);
          }
        } else if (allowExternal) {
          const errorMessage = validateExternal?.(value);
          if (errorMessage == null) {
            setRecipientInputStatus("valid");
            setRecipientInputValue("");

            onExternalSelected?.(value);
          } else {
            setRecipientInputStatus("invalid");
            setRecipientInputStatusMessage(errorMessage);
            setRecipientInputValue(value);
          }
        } else {
          setRecipientInputStatus("invalid");
          setRecipientInputStatusMessage(noMatchLabel);
          setRecipientInputValue(value);
        }
      } else {
        setRecipientInputStatus("valid");
        setRecipientInputValue(inputValue);
      }
    },
    [
      allowExternal,
      filterPredicate,
      noMatchLabel,
      onExternalSelected,
      onUserSelected,
      userList,
      validateExternal,
      validateUser,
    ]
  );

  // hack alert: we can't easily get rid of the suggestion list after the user
  // presses Enter at the input box, so we resort to clicking the first suggestion
  const onEnter = useCallback(
    (
      event:
        | React.KeyboardEvent<HTMLInputElement>
        | React.MouseEvent<HTMLButtonElement>,
      newRecipient: string
    ) => {
      const firstOption = document.getElementById(
        `${elementId}-suggestions-list`
      )?.firstElementChild?.firstElementChild as HTMLElement;
      if (firstOption != null) {
        firstOption.click();
      } else {
        // if no suggestion is visible, proceed normally
        onChange(event, newRecipient);
      }
    },
    [elementId, onChange]
  );

  return (
    <HvInput
      id={elementId}
      className={classes.input}
      classes={{ suggestionList: classes.suggestionList }}
      value={recipientInputValue}
      status={recipientInputStatus}
      statusMessage={recipientInputStatusMessage}
      suggestionListCallback={suggestionHandler}
      onEnter={onEnter}
      onChange={onChange}
      {...others}
    />
  );
};

export default UserSearchInput;
